import React, { useEffect, useState } from 'react';
import Dialog from 'src/components/common/Dialog';
import useFetch from 'use-http';
import PersonnelToast from '../../components/PersonnelToast';
import { useDispatch } from 'react-redux';

import Button from 'src/components/controls/forms/redesign/FormButton';

import '../../BenefitStyleSheet.scss';
import LoadingSpinner from 'src/components/common/LoadingSpinner';
import moment from 'moment';
import TSDILandman from 'src/models/personnel/TSDILandman';
import EmployeePtoHistory from 'src/models/personnel/EmployeePtoHistory';
import EmployeePtoInformation from 'src/models/personnel/EmployeePtoInformation';
import VacationBankDialog from './VacationBankPanel';

interface IPtoDashboardDialogProps {
    dialogName: string;
    hideDialog: () => void;
    showToast: (message: string, color?: string, persistent?: boolean) => void;
    selectedPersonnel: TSDILandman;
}

const PtoDashboardDialog = (props: IPtoDashboardDialogProps) => {
    const { get, post, response } = useFetch();
    const [toast, setToast] = useState<{ message: string; color?: string, persistent?: boolean; } | null>(null);
    const [ptoHistory, setPtoHistory] = useState<Array<EmployeePtoHistory>>();
    const [isLoading, setIsLoading] = useState<boolean>();
    const [selectedEmployee, setSelectedEmployee] = useState<EmployeePtoHistory>();

    const [employeePtoInformation, setEmployeePtoInformation] = useState<Array<EmployeePtoInformation>>([]);
    const [employeeVacationPtoInformation, setVacationEmployeePtoInformation] = useState<Array<EmployeePtoInformation>>([]);
    const [employeePersonalPtoInformation, setPersonalEmployeePtoInformation] = useState<Array<EmployeePtoInformation>>([]);
    const [employeeHolidayPtoInformation, setHolidayEmployeePtoInformation] = useState<Array<EmployeePtoInformation>>([]);

    const [showVacationBank, setShowVacationBank] = useState<boolean>(false);

    const dispatch = useDispatch();

    useEffect(() => {
        onCreate();
    }, []);

    useEffect(() => {
        if (props.selectedPersonnel && ptoHistory) {
            let found = ptoHistory?.find(history => history.tsdNo === props.selectedPersonnel?.tsdNo);
            setSelectedEmployee(found);
        }
    }, [props.selectedPersonnel]);

    useEffect(() => {
        getEmployeePtoInfo();
    }, [selectedEmployee]);

    // updates subtables
    useEffect(() => {
        if (employeePtoInformation !== undefined) {
            setVacationEmployeePtoInformation(employeePtoInformation?.filter((pto) => pto.type === 'V'));
            setPersonalEmployeePtoInformation(employeePtoInformation?.filter((pto) => pto.type === 'P'));
            setHolidayEmployeePtoInformation(employeePtoInformation?.filter((pto) => pto.type === 'H'));
        }
    }, [employeePtoInformation]);

    // formatting dates for each of the subtables
    useEffect(() => {
        employeeVacationPtoInformation?.forEach((info: EmployeePtoInformation) => {
            info.date = info.date ? (moment(info.date).format('MM-DD-YYYY') as unknown as Date) : null;
        });
    }, [employeeVacationPtoInformation]);

    useEffect(() => {
        employeePersonalPtoInformation?.forEach((info: EmployeePtoInformation) => {
            info.date = info.date ? (moment(info.date).format('MM-DD-YYYY') as unknown as Date) : null;
        });
    }, [employeePersonalPtoInformation]);

    useEffect(() => {
        employeeHolidayPtoInformation?.forEach((info: EmployeePtoInformation) => {
            info.date = info.date ? (moment(info.date).format('MM-DD-YYYY') as unknown as Date) : null;
        });
    }, [employeeHolidayPtoInformation]);

    useEffect(() => {
        if (selectedEmployee !== undefined && selectedEmployee !== null) {
            handleSelectRow(selectedEmployee);
        }
    }, [selectedEmployee]);

    const onCreate = () => {
        initializeData();
    };

    const initializeData = async () => {
        await getActiveEmployees();
    };

    const showToast = (message: string, color?: string, persistent?: boolean) => {
        setToast({ message, persistent, color });
    };

    const getActiveEmployees = async () => {
        setIsLoading(true);
        setPtoHistory(null);

        let results = await get(`/v1.0/landmen/benefits/pto-dashboard`);

        if (response.ok && results.length > 0) {
            setIsLoading(false);

            results.forEach((emp: EmployeePtoHistory) => {
                emp.hireDate = emp.hireDate ? (moment(emp.hireDate).format('MM-DD-YYYY') as unknown as Date) : null;
                emp.beginningDate = emp.beginningDate ? (moment(emp.beginningDate).format('MM-DD-YYYY') as unknown as Date) : null;
                emp.endDate = emp.endDate ? (moment(emp.endDate).format('MM-DD-YYYY') as unknown as Date) : null;
                emp.currentDate = emp.currentDate ? (moment(emp.currentDate).format('MM-DD-YYYY') as unknown as Date) : null;
            });

            setPtoHistory(results);

            let found = results.find((history: EmployeePtoHistory) => history.tsdNo === props.selectedPersonnel?.tsdNo);
            setSelectedEmployee(found);

        } else {
            console.log(response);
            showToast(`Unable to fetch active employees: ${response}`, `rgba(255,0,0,0.5)`);
        }

        setIsLoading(false);
    };

    // select personnel from row
    const handleSelectRow = async (emp: EmployeePtoHistory) => {
        let result = await get(`v1.0/landmen/personnel/${emp?.tsdNo}/get-by-tsd-no`);

        if (response.ok && result) {
            setSelectedPersonnel(result);
        } else {
            console.log('unable to find personnel');
        }
    };

    const getEmployeePtoInfo = async () => {
        let data = {
            tsdNo: selectedEmployee?.tsdNo,
            beginningDate: moment(selectedEmployee?.beginningDate).format('YYYY-MM-DD') as unknown as Date,
            endDate: moment(selectedEmployee?.endDate).format('YYYY-MM-DD') as unknown as Date
        };

        let result = await post(`v1.0/landmen/benefits/employee-pto-information`, data);

        let ptoArray: EmployeePtoInformation[] = [];

        if (Array.isArray(result)) {
            ptoArray = result;
        } else if (Array.isArray(result?.data)) {
            ptoArray = result.data;
        }

        setEmployeePtoInformation(ptoArray);
    };

    /**
     * Sets the selected Personnel to the store if the given ID is valid and it's not already the same personnel
     * @param personnel - personnel payload
     */
    const setSelectedPersonnel = async (personnel: TSDILandman) => {
        if (personnel) {
            personnel?.lmNo !== props.selectedPersonnel?.lmNo
                ? dispatch({ type: '[personnel] SET_SELECTED_PERSONNEL', payload: personnel })
                : showToast('Selected personnel already set', `rgba(78,117,154,0.5)`);
        } else {
            showToast('Selected personnel is undefined', `rgba(255,0,0,0.5)`);
        }
    };

    const hideDialog = () => {
        setShowVacationBank(false);
    };

    return (
        <Dialog title="PTO Dashboard" dialogName={props.dialogName} bodyStyle={{ width: '85%', height: '85%', overflow: 'auto' }}>
            <div>
                {toast && (
                    <PersonnelToast
                        message={toast.message}
                        color={toast.color}
                        onClose={() => setToast(null)}
                        persistent={toast.persistent}
                    />
                )}
            </div>
            <div>
                <div style={{ overflowY: 'scroll', maxHeight: '30rem' }}>
                    <table style={{ width: '100%' }}>
                        <thead>
                            <tr style={{ background: '#d6d6d6' }}>
                                <th style={{ width: '3%' }} className="sticky-header">Emp No</th>
                                <th style={{ width: '8%' }} className="sticky-header">Last</th>
                                <th style={{ width: '8%' }} className="sticky-header">First</th>
                                <th style={{ width: '3%' }} className="sticky-header">Middle</th>
                                <th style={{ width: '8%' }} className="sticky-header">AKA Name</th>
                                <th style={{ width: '8%' }} className="sticky-header">Hire Date</th>
                                <th style={{ width: '8%' }} className="sticky-header">Ann Date</th>
                                <th style={{ width: '3%' }} className="sticky-header">Vac Hours</th>
                                <th style={{ width: '3%' }} className="sticky-header">Vac Used</th>
                                <th style={{ width: '3%' }} className="sticky-header">Vac Avl</th>
                                <th style={{ width: '3%' }} className="sticky-header">Per Hours</th>
                                <th style={{ width: '3%' }} className="sticky-header">Per Used</th>
                                <th style={{ width: '3%' }} className="sticky-header">Per Avl</th>
                                <th style={{ width: '3%' }} className="sticky-header">Hol Hours</th>
                                <th style={{ width: '3%' }} className="sticky-header">Hol Used</th>
                                <th style={{ width: '3%' }} className="sticky-header">Hol Avl</th>
                            </tr>
                        </thead>
                        {isLoading && (
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    height: '50vh',
                                    width: '100%',
                                }}
                            >
                                <LoadingSpinner />
                            </div>
                        )}
                        {ptoHistory?.map((emp: EmployeePtoHistory, index: number) => {
                            return (
                                <tbody key={index}>
                                    <tr
                                        key={`p-${index}`}
                                        onClick={() => setSelectedEmployee(emp)}
                                        style={{ backgroundColor: props.selectedPersonnel?.tsdNo === emp?.tsdNo ? 'yellow' : undefined }}
                                    >
                                        <td style={{ width: '3%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            {emp?.tsdNo}
                                        </td>
                                        <td style={{ width: '8%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            {emp?.lastName}
                                        </td>
                                        <td style={{ width: '8%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            {emp?.firstName}
                                        </td>
                                        <td style={{ width: '3%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            {emp?.middleName}
                                        </td>
                                        <td style={{ width: '8%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            {emp?.akaName}
                                        </td>
                                        <td style={{ width: '8%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            {emp?.hireDate}
                                        </td>
                                        <td style={{ width: '8%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            {emp?.beginningDate}
                                        </td>
                                        <td style={{ width: '3%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            {emp?.vacationHours?.toFixed(2)}
                                        </td>
                                        <td style={{ width: '3%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            {emp?.vacationUsed?.toFixed(2)}
                                        </td>
                                        <td style={{ width: '3%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            {emp?.vacationAvaliable?.toFixed(2)}
                                        </td>
                                        <td style={{ width: '3%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            {emp?.personalHours?.toFixed(2)}
                                        </td>
                                        <td style={{ width: '3%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            {emp?.personalUsed?.toFixed(2)}
                                        </td>
                                        <td style={{ width: '3%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            {emp?.personalAvaliable?.toFixed(2)}
                                        </td>
                                        <td style={{ width: '3%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            {emp?.holidayHours?.toFixed(2)}
                                        </td>
                                        <td style={{ width: '3%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            {emp?.holidayUsed?.toFixed(2)}
                                        </td>
                                        <td style={{ width: '3%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            {emp?.holidayAvaliable?.toFixed(2)}
                                        </td>
                                    </tr>
                                </tbody>
                            );
                        })}
                    </table>
                </div>
                <hr />
                <br />
                <h3>PTO Breakdown {selectedEmployee ? ' for ' + selectedEmployee.akaName : ''}</h3>
                <div style={{ display: 'flex', gap: '1.5rem' }}>
                    {/* vacation hours info */}
                    <div style={{ width: '25%' }}>
                        <h4>Vacation Hours</h4>
                        <div style={{ overflowY: 'auto', maxHeight: '20rem', width: '100%' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr>
                                        <th style={{ width: '25%' }} className="sticky-header">
                                            Date
                                        </th>
                                        <th style={{ width: '25%' }} className="sticky-header">
                                            Hours
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {employeeVacationPtoInformation?.map((info: EmployeePtoInformation, index: number) => {
                                        return (
                                            <tr key={index}>
                                                <td style={{ width: '25%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                    {info.date}
                                                </td>
                                                <td style={{ width: '25%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                    {info.hours?.toFixed(2)}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <hr />
                        <Button text="Vacation Bank" style={{ width: '50%' }} onClick={() => setShowVacationBank(true)} intent="secondary" />
                    </div>
                    {/* personal hours info */}
                    <div style={{ width: '25%' }}>
                        <h4>Personal Hours</h4>
                        <div style={{ overflowY: 'auto', maxHeight: '20rem', width: '100%' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr>
                                        <th style={{ width: '25%' }} className="sticky-header">
                                            Date
                                        </th>
                                        <th style={{ width: '25%' }} className="sticky-header">
                                            Hours
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {employeePersonalPtoInformation?.map((info: EmployeePtoInformation, index: number) => {
                                        return (
                                            <tr key={index}>
                                                <td style={{ width: '25%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                    {info.date}
                                                </td>
                                                <td style={{ width: '25%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                    {info.hours?.toFixed(2)}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    {/* holiday hours info */}
                    <div style={{ width: '25%' }}>
                        <h4>Holiday Hours</h4>
                        <div style={{ overflowY: 'auto', maxHeight: '20rem', width: '100%' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr>
                                        <th style={{ width: '25%' }} className="sticky-header">
                                            Date
                                        </th>
                                        <th style={{ width: '25%' }} className="sticky-header">
                                            Hours
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {employeeHolidayPtoInformation?.map((info: EmployeePtoInformation, index: number) => {
                                        return (
                                            <tr key={index}>
                                                <td style={{ width: '25%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                    {info.date}
                                                </td>
                                                <td style={{ width: '25%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                    {info.hours?.toFixed(2)}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                <div className="button-row" style={{ paddingBottom: '1rem' }}>
                    <Button text="Close" style={{ width: '25%' }} onClick={() => props.hideDialog()} intent="primary" />
                </div>

                {showVacationBank && <div className="popup-overlay">
                    {showVacationBank && (
                        <div className="popup-overlay-panel-vacation">
                            <VacationBankDialog
                                selectedPersonnel={props?.selectedPersonnel}
                                closePanel={hideDialog}
                            />
                        </div>
                    )}
                </div>}
            </div>
        </Dialog>
    );
};

export default PtoDashboardDialog;



// WEBPACK FOOTER //
// ./src/components/personnel/dialogs/benefits/PtoDashboardDialog.tsx